import { Component, Prop, State, h, Element, Listen } from '@stencil/core';

@Component({
  tag: 'vs-tooltip',
  styleUrls: {
    default: './vs-tooltip.css',
    custom: './vs-tooltip-custom.css'
  },
  shadow: false
})
export class VsTooltip {

  @Element() tooltipElement: HTMLElement;

  /**
   * @shortDescription ID of the tooltip
   * @controlName tooltipId
   */
  @Prop() tooltipId: string = 'tooltip';

  /**
   * @shortDescription Header text of the tooltip.
   * @controlName tooltipHeader
   */
  @Prop() tooltipHeader: string = '';
  /**
   * @shortDescription Body text of the tooltip.
   * @controlName tooltipBody
   */
  @Prop() tooltipBody: string = '';
  /**
   * @shortDescription Label for accessibility when the tooltip is open.
   * @controlName allyOpenLabel
   */
  @Prop() allyOpenLabel: string = 'Open Tooltip';
  /**
   * @shortDescription Label for accessibility when the tooltip is closed.
   * @controlName allyCloseLabel
   */
  @Prop() allyCloseLabel: string = 'Close tooltip, tabbing off will close the tooltip as well';

  /**
   @shortDescription Style mode of the component
   @controlName mode */
  @Prop() mode: string;

  @State() show: boolean = false;
  @State() isMobile: boolean = false;
  @State() tooltipArrowClass: string = '';

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.isMobile = window.innerWidth < 768;
    this.show = false;
  }

  private close(dontReturnFocus = false) {
    this.show = false;
    if((this.tooltipElement.querySelector('.tooltipbackground') as HTMLElement)){
      (this.tooltipElement.querySelector('.tooltipbackground') as HTMLElement).style.background = 'transparent';
    }
    (this.tooltipElement.querySelector('.vs-tooltip-dialog') as HTMLElement).style.display = 'none';
    if(!dontReturnFocus) {
      (this.tooltipElement.querySelector('.tooltipbutton') as HTMLElement).focus();
    }
  }

  private open = () => {
    let innerWidth = window.innerWidth;
    this.show = true;
    const tooltipDialog = (this.tooltipElement.querySelector('.vs-tooltip-dialog') as HTMLElement);
    const tooltipButton = (this.tooltipElement.querySelector('.tooltipbutton') as HTMLElement);
    tooltipDialog.style.display = 'block';
    tooltipDialog.style.zIndex = '10';
    const dialogPosition = tooltipDialog.getBoundingClientRect();
    const buttonPosition = tooltipButton.getBoundingClientRect();
    let rightPosition = innerWidth - buttonPosition.right;
    let x = -(dialogPosition.width / 2) + buttonPosition.width / 2 + 5;
    let y = -105;
    let isAbove = true;

    // 0-left, 1-center, 2-right
    let horizontalPosition = 1;

    // tooltip above button (adjusting based on tooltip dialog height)
    if (dialogPosition.height > 100) {
      y = y - (dialogPosition.height - 100);
    }

    //tooltip below button
    if (buttonPosition.top < dialogPosition.height) {
      y = buttonPosition.height + 10;
      isAbove = false;
    }
    tooltipDialog.style.top = `${y}px`;

    // Determine left/right/center

    // Left aligned
    if (buttonPosition.left < dialogPosition.width / 2) {
      x = 5;
      horizontalPosition = 0;
    }

    // Right aligned
    if (rightPosition < dialogPosition.width / 2) {
      x =
        -(dialogPosition.width - rightPosition) +
        buttonPosition.width / 2 +
        15;
      horizontalPosition = 2;
    }

    this.tooltipArrowClass = VsTooltip.findTooltipArrowPosition(
      isAbove,
      horizontalPosition
    );

    tooltipDialog.style.left = `${x}px`;
    tooltipDialog.style.position = 'absolute';
    if (this.isMobile) {
      tooltipDialog.style.position = 'fixed';
      tooltipDialog.style.left = '50%';
      tooltipDialog.style.top = '50%';
    }
  }

  private static findTooltipArrowPosition(isAbove, horizontalPosition) {
    if (isAbove && horizontalPosition == 0) {
      return 'vs-bottom-left';
    } else if (isAbove && horizontalPosition == 1) {
      return '';
    } else if (isAbove && horizontalPosition == 2) {
      return 'vs-bottom-right';
    } else if (!isAbove && horizontalPosition == 0) {
      return 'vs-top-left';
    } else if (!isAbove && horizontalPosition == 1) {
      return 'vs-top-center';
    } else if (!isAbove && horizontalPosition == 2) {
      return 'vs-top-right';
    }
  }

  private focusout_closeTooltip = (event) => {
    if (!this.tooltipElement.contains(event.relatedTarget)) {
      this.close(true);
    }
  }

  private onkeydown_closeTooltipOnEsc = (event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.close();
    }
  }

  private onclick_toggleTooltip = () => {
    if (this.show) {
      this.close();
    } else {
      this.open();
    }
  }

  private onclick_closeTooltip = () => {
    this.close();
  }

  private onmouseover_toggleTooltip = () => {
    this.open();
  }

  private onkeydown_closeTooltip = (event: KeyboardEvent) => {
    this.onkeydown_closeTooltipOnEsc(event);
    if (event.code === 'Tab' && !event.shiftKey) {
      this.close();
    }
  }

  render() {
    return (
      <div class="vs-tooltip"
           onFocusout={this.focusout_closeTooltip.bind(this)}
           onKeyDown={this.onkeydown_closeTooltipOnEsc.bind(this)}>
        <button type="button" class="visa-icon visa-icon-info tooltipbutton"
                aria-label={this.allyOpenLabel}
                onClick={this.onclick_toggleTooltip.bind(this)}
                onMouseEnter={this.onmouseover_toggleTooltip.bind(this)}>
        </button>
        <div id={'id-tooltip-'+this.tooltipId} class="vs-tooltip-dialog"
             role="dialog" aria-modal={this.show? "true" : undefined}>
          {this.tooltipHeader &&
            <h2 class="vs-lead" role='alert'
                tab-index={this.show? "0" : "-1"}>{this.tooltipHeader}</h2>}
          {this.tooltipBody && <p role='alert' aria-live='polite'
                                                  tab-index={this.show? "0" : "-1"}
                                                  innerHTML={this.tooltipBody}/>}
          <button type="button" class="vs-close" aria-label={this.allyCloseLabel}
                  onClick={this.onclick_closeTooltip.bind(this)}
                  onKeyDown={this.onkeydown_closeTooltip.bind(this)}>
          </button>

          <i aria-hidden="true" class={this.tooltipArrowClass}/>
        </div>
        {this.show &&
          <div class="tooltipbackground vs-tooltip-background" onClick={this.onclick_closeTooltip.bind(this)} />
        }
      </div>
    );
  }
}
